import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef
} from 'react'
import { useRouter } from 'next/router'
import {
  Box,
  Flex,
  Text,
  Button,
  Image,
  Shimmer,
  Relative,
  Absolute
} from 'pcln-design-system'
import { isEmpty, join, keys } from 'ramda'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import pclnExperimentation from '@pcln/experimentation'
import { Calendar } from '@pcln/date-picker'
import { useForm } from 'react-hook-form'
import { format, addDays } from 'date-fns'
import Sticky from 'react-stickynode'
import { Pin } from 'pcln-icons'
import { getHotelDateRange } from '../../../utils/date-helper'
import SearchFormButton from '../../SearchFormButton'
import TypeAhead from '../../TypeAhead'
import { PopoverContainer } from '../../Popover'
import ShadowEffect from '../../ShadowEffect'
import CalendarWrapperBox from '../../CalenderWrapperBox'
import DateField from '../../DateField'
import TravelerSelection from '../../TravelerSelection/TravelerSelection'
import { VP_DISCLAIMER } from '../../TravelerSelection/constants'
import BootstrapDataContext from '../../../context/BootstrapDataContext'
import useTabGroup from '../../CustomHooks/useTabGroup'
import StickyEffect from '../../StickyEffect'
import {
  PromoDealType,
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SEM_SEARCH_TYPE
} from '../types'
import {
  searchFormSchema,
  isInfantLimitReachedPackages,
  INFANT_CAPACITY_LIMIT
} from './validation'
import { fields, initialState, groupDealsURL } from './utils'
import {
  ADD_PRODUCT_OPTIONS,
  CALENDAR_POPOVER_SELECTOR,
  PromoDeal,
  FLY,
  DRIVE,
  STAY
} from '../constants'
import ProductCheckBox from '../../ProductCheckbox/ProductCheckbox'
import analytics from '../../../utils/analytics'
import {
  ANALYTICS_ACTION_HOTELS_SEARCH,
  ANALYTICS_CATEGORIES,
  INVALID
} from '../../../constants/analytics'
import { LOCATION_SEARCH_TYPE } from '../../../types'
import useCityNameInBtnTextExperiment from '../../CustomHooks/useCityNameInBtnTextExperiment'
import useFetchCityMap from '../../CustomHooks/useFetchCityMap'
import usePrePopPrepaidSearch from '../../CustomHooks/usePrePopPrepaidSearch'
import ExperimentContext from '../../../context/ExperimentContext'

const StyledCancellationBox = styled<{ isMobile: boolean }>(Box)`
  position: ${props => (props.isMobile ? '' : 'absolute')};
  margin-top: ${props => (props.isMobile ? '12px' : '0px')};
  margin-bottom: ${props => (props.isMobile ? '12px' : '0px')};
  top: ${props => (props.isMobile ? '' : '68px')};
  left: ${props => (props.isMobile ? '' : '10px')};
`

const StyledRelative = styled(Relative)`
  overflow: hidden;
`

const StyledAbsolute = styled(Absolute)`
  top: ${themeGet('space.4')}px;
  right: ${themeGet('space.3')}px;
`

// Needed to prevent prop type errors with a js component and typescript
const TypeAheadJS: any = TypeAhead
const DateFieldJS: any = DateField

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE) => void
  addPackagesSupport?: boolean
  promoDeal?: PromoDealType
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
}

export default function Form({
  onSubmit,
  promoDeal,
  setLocation = () => {
    /* if pre-pop isn't used*/
  }
}: FormType) {
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const { isMobile = false, prePopData } = useContext(BootstrapDataContext)
  const prePaidPrePop = usePrePopPrepaidSearch()

  const {
    endLocation,
    startDate: prePopStartDate,
    endDate: prePopEndDate,
    roomCount
  } = prePopData?.hotels ?? prePaidPrePop ?? {}
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const enableStickyEffect = !isMobile && !promoDeal
  const { query, pathname } = useRouter()
  const isHomepage = pathname === '/'
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]
  const ORIGINAL_MOBILE_CITY_MAP_HEIGHT = 130
  const MOBILE_CITY_MAP_HEIGHT_CONTAINER = 80

  const { SEMSearch, SEMSEARCH, 'ambig-hotel-city': ambigHotelCity } = query
  const initialValues = {
    ...initialState,
    ...(endLocation && { endLocation: endLocation as LOCATION_SEARCH_TYPE }),
    ...(prePopStartDate && { startDate: prePopStartDate }),
    ...(prePopEndDate && { endDate: prePopEndDate }),
    ...(roomCount && { roomCount })
  }
  const {
    clearError,
    register,
    unregister,
    setValue,
    handleSubmit,
    watch,
    triggerValidation,
    errors
  } = useForm<HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange'
  })

  const [cityMapWidth, setCityMapWidth] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    setCityMapWidth(window.screen.width)
  }, [])

  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  const {
    endLocation: watchEndLocation,
    startLocation: watchStartLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    tripType: watchTripType
  } = watch()

  useEffect(() => {
    if (watchEndLocation) {
      setLocation(watchEndLocation)
    }
  }, [watchEndLocation, setLocation])

  useEffect(() => {
    if (
      isMobile &&
      !isHomepage &&
      window?.PCLN?.pclnEventDispatcher?.dispatch
    ) {
      pclnExperimentation.fireImpression('MKTG_MWEB_HTL_PROD_HP_APP_MODAL')
    }
  }, [isMobile, isHomepage])

  const { MKTG_MWEB_HTL_PROD_HP_APP_MODAL } = useContext(ExperimentContext)

  const searchBtnText = useCityNameInBtnTextExperiment(
    watchTripType,
    watchEndLocation
  )
  const hasFlight = watchTripType.includes(FLY)
  const hasCar = watchTripType.includes(DRIVE)

  const { lat, lon, cityName } =
    (prePopData?.hotels?.endLocation as LOCATION_SEARCH_TYPE) ?? {}

  const showCityMapInMobile =
    isMobile && !hasFlight && !hasCar && !isHomepage && !!cityName

  const showCityDisambiguation = !!ambigHotelCity && !!cityName && !isHomepage

  const { cityMapURL, fetchCityMap } = useFetchCityMap(
    showCityMapInMobile
      ? {
          lat,
          lon,
          width: cityMapWidth,
          height: ORIGINAL_MOBILE_CITY_MAP_HEIGHT
        }
      : {
          lat: undefined,
          lon: undefined,
          width: undefined,
          height: undefined
        }
  )

  useEffect(() => {
    if (!prePopData && prePaidPrePop) {
      setValue('startDate', prePaidPrePop?.startDate)
      setValue('endDate', prePaidPrePop?.endDate)
      setValue('endLocation', prePaidPrePop?.endLocation)
    }
  }, [prePaidPrePop, prePopData, setValue])

  useEffect(() => {
    const shouldPopulateDates = !watchStartDate && !watchEndDate
    const start = shouldPopulateDates
      ? format(new Date(), 'yyyyMMdd')
      : watchStartDate
    const end = shouldPopulateDates
      ? format(addDays(new Date(), 1), 'yyyyMMdd')
      : watchEndDate
    if (shouldPopulateDates) {
      setValue('startDate', start)
      setValue('endDate', end)
    }
  }, [setValue, watchStartDate, watchEndDate])

  const isOneLineSearch =
    !isMobile &&
    !promoDeal &&
    (isStickyInEffect || !isHomepage) &&
    (isStickyInEffect || !hasFlight)

  // eslint-disable-next-line no-nested-ternary
  const typeaheadWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 24 / 100]
      : [1, null, 27 / 100]
    : hasFlight
    ? [1, null, 1 / 2, null, 1 / 2]
    : [1, null, 1 / 2, null, 1]

  // eslint-disable-next-line no-nested-ternary
  const calendarWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 24 / 100]
      : [1, null, 31 / 100]
    : hasFlight
    ? [1, null, 2 / 5, null, 1 / 3]
    : [1, null, 1 / 2, null, 1 / 3]

  // eslint-disable-next-line no-nested-ternary
  const roomSelectionWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 18 / 100]
      : [1, null, 23 / 100]
    : hasFlight
    ? [1, null, 2 / 5, null, 1 / 3]
    : [1, null, 1 / 2, null, 1 / 3]

  // eslint-disable-next-line no-nested-ternary
  const searchButtonWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 10 / 100]
      : [1, null, 19 / 100]
    : hasFlight
    ? [1, null, 1 / 5, null, 1 / 3]
    : [1, null, 1 / 2, null, 1 / 3]

  const hotelPlannerLink = groupDealsURL({
    location: watchEndLocation ? watchEndLocation.itemName : '',
    startDate: watchStartDate,
    endDate: watchEndDate
  })

  const hotelPlannerLinkAttrsProp = {
    href: hotelPlannerLink,
    target: '_blank'
  }

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      setIsStickyInEffect(true)
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const showPackageOptions = () =>
    !promoDeal && (
      <Box pl={2} mb={3}>
        <ProductCheckBox
          category={ANALYTICS_CATEGORY}
          productOptions={ADD_PRODUCT_OPTIONS}
          tripType={watchTripType}
          label="Bundle + Save"
          setValue={setValue}
          color={isStickyInEffect ? 'text.lightest' : 'text.light'}
          isStickySearch={isStickyInEffect}
        />
      </Box>
    )

  const showCurrentLocation = !promoDeal && isMobile

  useEffect(() => {
    clearError('startLocation')
  }, [clearError, watchStartLocation])
  useEffect(() => {
    clearError('endLocation')
  }, [clearError, watchEndLocation])
  useEffect(() => {
    clearError('startDate')
  }, [clearError, watchStartDate])
  useEffect(() => {
    clearError('endDate')
  }, [clearError, watchEndDate])
  useEffect(() => {
    triggerValidation('childrenCount')
  }, [
    triggerValidation,
    watchStartLocation,
    watchEndLocation,
    watchChildrenAges,
    watchAdultCount,
    watchChildrenCount
  ])

  useEffect(() => {
    triggerValidation('roomCount')
  }, [
    triggerValidation,
    watchChildrenCount,
    watchRoomCount,
    watchAdultCount,
    watchTripType
  ])

  useEffect(() => {
    if (!isEmpty(errors)) {
      const label = `${INVALID} - ${join(' | ', keys(errors))}`
      analytics.fireEvent({
        action: isStickyInEffect
          ? 'Hotel Sticky Search Button'
          : ANALYTICS_ACTION_HOTELS_SEARCH,
        category: ANALYTICS_CATEGORY,
        label
      })
    }
  }, [errors, ANALYTICS_CATEGORY, isStickyInEffect])

  const itemSelected = useCallback(
    (item: LOCATION_SEARCH_TYPE) => {
      const gaTracking = () => {
        analytics.fireEvent({
          action: 'Field click',
          category: ANALYTICS_CATEGORY,
          label: 'Typeahead'
        })
      }
      if (item) {
        gaTracking()
      }

      if (showCityMapInMobile && item) {
        fetchCityMap({
          lat: item?.lat,
          lon: item?.lon,
          width: window.screen.width,
          height: ORIGINAL_MOBILE_CITY_MAP_HEIGHT
        })
      }

      if (!isMobile && !!item) {
        focusNextElement()
      } else if (isMobile && !!item && document.activeElement) {
        ;(document.activeElement as HTMLElement).blur()
      }
    },
    [
      isMobile,
      ANALYTICS_CATEGORY,
      showCityMapInMobile,
      fetchCityMap,
      focusNextElement
    ]
  )

  const ambiguousElement = useRef<HTMLInputElement>()

  function registerAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousElement.current = ref
  }
  const clarifyAmbiguity = useCallback(() => {
    const city: string = endLocation?.cityName
    return setTimeout(() => {
      if (ambiguousElement.current && city) {
        ambiguousElement.current.focus()
        const ambiguousCity: any = { ...endLocation, itemName: city }
        setValue('endLocation', ambiguousCity)
      }
    }, 0)
  }, [endLocation, setValue])

  return (
    <form
      data-testid={promoDeal ? 'express-deal-form' : 'form'}
      aria-label={
        promoDeal ? 'hotel-express-deal-search-form' : 'hotel-search-form'
      }
      onSubmit={handleSubmit((data: HOTEL_FORM_STATE_TYPE) =>
        onSubmit({
          ...data,
          gaCategory: ANALYTICS_CATEGORY,
          ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
          ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
          gaAction: isStickyInEffect
            ? 'Hotel Sticky Search Button'
            : ANALYTICS_ACTION_HOTELS_SEARCH,
          mapView: false
        })
      )}
    >
      <>
        <StickyEffect
          enabled={enableStickyEffect}
          isStickyInEffect={isStickyInEffect}
          handleStickyEffectStateChange={handleStickyEffectStateChange}
          stickyHeight={120}
        >
          <Flex
            key={0}
            mx={-2}
            flexWrap={['wrap', isOneLineSearch ? null : 'wrap']}
          >
            {hasFlight && (
              <Box
                width={typeaheadWidth}
                mb={3}
                pl={2}
                pr={isOneLineSearch ? 1 : 2}
                data-testid="DASH_TAB_HOTELS_STARTLOCATION"
              >
                <ShadowEffect forProperty="location">
                  {({
                    disableShadowState
                  }: {
                    disableShadowState: () => void
                  }) => (
                    <TypeAheadJS
                      searchProduct="packages"
                      label="Where from?"
                      placeholder="Where from?"
                      searchKey="startLocation"
                      setValue={setValue}
                      errors={errors}
                      disableShadowState={disableShadowState}
                      defaultSelectedItem={watchStartLocation}
                      ref={registerTabElement}
                      showCurrentLocation={showCurrentLocation}
                      onItemSelect={itemSelected}
                    />
                  )}
                </ShadowEffect>
              </Box>
            )}
            <Box
              width={typeaheadWidth}
              mb={showCityDisambiguation ? 1 : 3}
              pl={hasFlight && isOneLineSearch ? 1 : 2}
              pr={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_ENDLOCATION"
            >
              <ShadowEffect forProperty="location">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TypeAheadJS
                    searchProduct={hasFlight ? 'packages' : 'hotels'}
                    label="Where to?"
                    placeholder="Where to?"
                    searchKey="endLocation"
                    setValue={setValue}
                    errors={errors}
                    disableShadowState={disableShadowState}
                    defaultSelectedItem={watchEndLocation}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                      if (showCityDisambiguation) {
                        registerAmbiguousElement(elem)
                      }
                    }}
                    showCurrentLocation={showCurrentLocation}
                    onItemSelect={itemSelected}
                  />
                )}
              </ShadowEffect>
              {showCityDisambiguation && (
                <Box py={[2, null, 1]}>
                  <Button
                    variation="link"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault()
                      clarifyAmbiguity()
                      analytics.fireEvent({
                        action: 'ambiguous-city',
                        category: ANALYTICS_CATEGORY,
                        label: cityName
                      })
                    }}
                  >
                    <Text fontSize={0}>
                      Looking for a different {cityName}?
                    </Text>
                  </Button>
                </Box>
              )}
            </Box>
            <Box
              width={calendarWidth}
              mb={3}
              px={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_DATES"
              id="hotel-date-range-box"
            >
              <ShadowEffect forProperty="hotel-date-range">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <PopoverContainer
                    open={isHotelCalendarOpen}
                    data-calendar
                    onDismiss={() => {
                      setIsHotelCalendarOpen(false)
                      disableShadowState()
                    }}
                    domSelectorForVisuallyContainedElement={
                      CALENDAR_POPOVER_SELECTOR
                    }
                  >
                    <DateFieldJS
                      id="hotel-date-range"
                      data-testid="hotel-date-range"
                      m={0}
                      ref={registerTabElement}
                      aria-expanded={isHotelCalendarOpen ? 'true' : 'false'}
                      error={(() => {
                        if (errors.startDate) {
                          return errors.startDate.message
                        }
                        if (errors.endDate) {
                          return errors.endDate.message
                        }
                      })()}
                      label="Check-in - Check-out"
                      name="hotelDates"
                      onFocus={() => {
                        setIsHotelCalendarOpen(true)
                      }}
                      value={getHotelDateRange({
                        startDate: watchStartDate,
                        endDate: watchEndDate
                      })}
                      isMobile={isMobile}
                    />
                    <CalendarWrapperBox>
                      <Calendar
                        startDate={watchStartDate}
                        endDate={watchEndDate}
                        maxDaysToSelect={29}
                        monthWidthPx={300}
                        onChange={(
                          _: unknown,
                          {
                            startDate,
                            endDate
                          }: { startDate: string; endDate: string }
                        ) => {
                          if (startDate && endDate) {
                            setIsHotelCalendarOpen(false)
                            disableShadowState()
                          }
                          setValue('startDate', startDate || '')
                          setValue('endDate', endDate || '')
                        }}
                        monthsToShow={2}
                        onDismiss={() => {
                          setIsHotelCalendarOpen(false)
                          disableShadowState()
                        }}
                        useDefaultFooter
                      />
                    </CalendarWrapperBox>
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </Box>
            <Box
              width={roomSelectionWidth}
              mb={3}
              px={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_NUMROOMS"
            >
              <ShadowEffect
                enabled
                forProperty="travelers-selection-readonly-input"
                alternativeBlurHandling
              >
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TravelerSelection
                    adultCount={watchAdultCount}
                    childrenAges={watchChildrenAges}
                    childrenCount={watchChildrenCount}
                    disableShadowState={disableShadowState}
                    hotelPlannerLinkAttrs={hotelPlannerLinkAttrsProp}
                    disclaimer={
                      watchTripType.includes(FLY) ? VP_DISCLAIMER : ''
                    }
                    isMobile={isMobile}
                    error={
                      errors?.childrenCount?.message ||
                      errors?.roomCount?.message ||
                      (isInfantLimitReachedPackages(
                        watchAdultCount,
                        watchChildrenAges,
                        watchTripType
                      ) &&
                        INFANT_CAPACITY_LIMIT) ||
                      ''
                    }
                    infantCount={0}
                    maxAdults={watchTripType === STAY ? 28 : undefined}
                    maxChildren={watchTripType === STAY ? 8 : undefined}
                    maxRooms={watchTripType === STAY ? 9 : undefined}
                    onDoneButtonClick={disableShadowState}
                    roomCount={watchRoomCount}
                    setValue={setValue}
                    showChildAgeSelectors
                    showInfantMenuItem={false}
                    showRoomMenuItem
                    showSubLabel={false}
                    showYouthMenuItem={false}
                    youthCount={0}
                  />
                )}
              </ShadowEffect>
            </Box>
            {isMobile && showPackageOptions()}
            <Relative
              width={searchButtonWidth}
              pl={isOneLineSearch ? 1 : 2}
              pr={2}
              mb={showCityMapInMobile ? 0 : 3}
              ml="center"
            >
              <SearchFormButton
                buttonText={searchBtnText}
                data-testid="HOTELS_SUBMIT_BUTTON"
                handleClick={() => {
                  if (
                    isMobile &&
                    !isHomepage &&
                    MKTG_MWEB_HTL_PROD_HP_APP_MODAL === 'VARIANT' &&
                    window?.PCLN?.pclnEventDispatcher?.dispatch !== undefined
                  ) {
                    window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
                  }
                }}
              />
              {promoDeal !== PromoDeal.COLLECTIONS &&
                promoDeal !== PromoDeal.EXPRESS_DEAL && (
                  <StyledCancellationBox isMobile={isMobile}>
                    <Text
                      fontSize={0}
                      color={isStickyInEffect ? 'text.lightest' : null}
                    >
                      Book a hotel with free cancellation for flexibility
                    </Text>
                  </StyledCancellationBox>
                )}
            </Relative>
            {showCityMapInMobile &&
              cityMapURL !== 'None' &&
              (!cityMapURL ? (
                <Box data-testid="city-map-shimmer" px={9} width={1}>
                  <Shimmer
                    height={MOBILE_CITY_MAP_HEIGHT_CONTAINER}
                    width={1}
                  />
                </Box>
              ) : (
                <StyledRelative
                  mb={10}
                  width={1}
                  height={MOBILE_CITY_MAP_HEIGHT_CONTAINER}
                >
                  <Image
                    src={cityMapURL}
                    alt={`Map of ${cityName}`}
                    px={9}
                    top="50%"
                    left="50%"
                    width={1}
                    height="auto"
                    position="relative"
                    transform="translate(-50%, -50%)"
                    data-testid="city-map-image"
                  />
                  <StyledAbsolute>
                    <Button
                      size="small"
                      data-testid="open-map-view"
                      onClick={handleSubmit((data: HOTEL_FORM_STATE_TYPE) =>
                        onSubmit({
                          ...data,
                          gaCategory: ANALYTICS_CATEGORY,
                          ...(SEMSearch && {
                            SEMSearch: SEMSearch as SEM_SEARCH_TYPE
                          }),
                          gaAction: isStickyInEffect
                            ? 'Hotel Sticky Search Button'
                            : ANALYTICS_ACTION_HOTELS_SEARCH,
                          mapView: true
                        })
                      )}
                    >
                      <Flex alignItems="center">
                        <Pin size={14} mr={1} />
                        <Text>View Hotels on Map</Text>
                      </Flex>
                    </Button>
                  </StyledAbsolute>
                </StyledRelative>
              ))}
            {!isMobile && showPackageOptions()}
          </Flex>
        </StickyEffect>
      </>
    </form>
  )
}
